import React, {memo, useCallback, useRef} from 'react';
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {alpha, SxProps, Theme} from '@mui/material/styles';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {C135_PAGE} from '@/pages/components/audit-program/c-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface C135ProgressViewProps {
    header: AuditProgramSectionVO;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

type C135Row = (typeof C135_PAGE.rows)[number];

const cellBorder = {borderColor: 'divider'};
const headSx = {
    ...cellBorder,
    fontWeight: 600,
    fontSize: '0.65rem',
    py: 0.75,
    px: 0.3,
    lineHeight: 1.2,
    verticalAlign: 'bottom' as const,
    whiteSpace: 'pre-line' as const,
};
const bodySx = {
    ...cellBorder,
    py: 0.35,
    px: 0.3,
    verticalAlign: 'middle' as const,
    overflow: 'hidden',
};

const nativeBaseSx: SxProps<Theme> = {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    m: 0,
    border: '1px solid',
    borderColor: 'rgba(0, 0, 0, 0.23)',
    borderRadius: 1,
    px: 0.5,
    py: '3px',
    fontSize: (theme) => theme.typography.body2.fontSize,
    lineHeight: 1.25,
    fontFamily: 'inherit',
    color: 'text.primary',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
    outline: 'none',
    '&:hover': {borderColor: 'text.primary'},
    '&:focus': {
        borderColor: 'primary.main',
        boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
    },
};

const CellInput = memo(function CellInput({
    value,
    onChange,
    align = 'left',
}: {
    value: string;
    onChange: (v: string) => void;
    align?: 'left' | 'center' | 'right';
}) {
    return (
        <Box
            component="input"
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            sx={{...nativeBaseSx, textAlign: align}}
        />
    );
});

const ProgressRow = memo(function ProgressRow({
    row,
    values,
    onChange,
}: {
    row: C135Row;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}) {
    const f = (k: string) => values[`${row.id}.${k}`] ?? '';
    const set = useCallback((k: string) => (v: string) => onChange(`${row.id}.${k}`, v), [onChange, row.id]);

    if (row.kind === 'note') {
        return (
            <TableRow>
                <TableCell sx={bodySx} colSpan={8}>
                    <Typography variant="body2" color="text.secondary" sx={{fontStyle: 'italic'}}>
                        {row.name}
                    </Typography>
                </TableCell>
            </TableRow>
        );
    }

    const bold = row.kind === 'total';

    return (
        <TableRow>
            <TableCell sx={bodySx}>
                <Typography variant="body2" fontWeight={bold ? 700 : 400}>
                    {row.name}
                </Typography>
            </TableCell>
            <TableCell sx={bodySx}>
                <CellInput value={f('budget')} onChange={set('budget')} align="right"/>
            </TableCell>
            <TableCell sx={bodySx}>
                <CellInput value={f('bfwd')} onChange={set('bfwd')} align="right"/>
            </TableCell>
            <TableCell sx={bodySx}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.35}}>
                    <CellInput value={f('dateFrom')} onChange={set('dateFrom')} align="center"/>
                    <Typography variant="caption" color="text.secondary">–</Typography>
                    <CellInput value={f('dateTo')} onChange={set('dateTo')} align="center"/>
                </Box>
            </TableCell>
            <TableCell sx={bodySx}>
                <CellInput value={f('cfwd')} onChange={set('cfwd')} align="right"/>
            </TableCell>
            <TableCell sx={bodySx}>
                <CellInput value={f('estimate')} onChange={set('estimate')} align="right"/>
            </TableCell>
            <TableCell sx={bodySx}>
                <CellInput value={f('total')} onChange={set('total')} align="right"/>
            </TableCell>
            <TableCell sx={bodySx}>
                <CellInput value={f('comments')} onChange={set('comments')}/>
            </TableCell>
        </TableRow>
    );
});

/** Excel C13.5 — Job Progress Report */
export default function C135ProgressView({header, values, onChange}: C135ProgressViewProps) {
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const stableOnChange = useCallback((key: string, value: string) => {
        onChangeRef.current(key, value);
    }, []);

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a13"
                refCode="C13.5"
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.4, mb: 1, mt: 0.5}}
            >
                {C135_PAGE.title}
            </Typography>

            <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} sx={{mb: 1.5}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0}}>
                    <Typography variant="body2" sx={{flexShrink: 0, fontWeight: 600}}>
                        Staff name:
                    </Typography>
                    <CellInput
                        value={values.staffName ?? ''}
                        onChange={(v) => onChange('staffName', v)}
                    />
                </Box>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0}}>
                    <Typography variant="body2" sx={{flexShrink: 0, fontWeight: 600}}>
                        Grade:
                    </Typography>
                    <CellInput
                        value={values.grade ?? ''}
                        onChange={(v) => onChange('grade', v)}
                    />
                </Box>
            </Stack>

            <TableContainer sx={{overflowX: 'auto'}}>
                <Table
                    size="small"
                    sx={{
                        width: '100%',
                        minWidth: 1100,
                        tableLayout: 'fixed',
                        borderCollapse: 'collapse',
                    }}
                >
                    <colgroup>
                        <col style={{width: '18%'}}/>
                        <col style={{width: '8%'}}/>
                        <col style={{width: '8%'}}/>
                        <col style={{width: '16%'}}/>
                        <col style={{width: '8%'}}/>
                        <col style={{width: '10%'}}/>
                        <col style={{width: '8%'}}/>
                        <col style={{width: '24%'}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx}/>
                            <TableCell sx={headSx} align="center">
                                Budget
                            </TableCell>
                            <TableCell sx={headSx} align="center">
                                b/fwd
                            </TableCell>
                            <TableCell sx={headSx} align="center">
                                Dates
                            </TableCell>
                            <TableCell sx={headSx} align="center">
                                c/fwd
                            </TableCell>
                            <TableCell sx={headSx} align="center">
                                Estimate to complete
                            </TableCell>
                            <TableCell sx={headSx} align="center">
                                TOTAL
                            </TableCell>
                            <TableCell sx={headSx} align="center">
                                Comments
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {C135_PAGE.rows.map((row) => (
                            <ProgressRow
                                key={row.id}
                                row={row}
                                values={values}
                                onChange={stableOnChange}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
